import * as RadioGroupPrimitive from '@radix-ui/react-radio-group';
import { forwardRef, useId, type ComponentPropsWithoutRef, type ReactNode } from 'react';
import { cn } from '@/shared/utils/cn';

export const RadioGroup = forwardRef<
  HTMLDivElement,
  ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Root>
>(function RadioGroup({ className, ...rest }, ref) {
  return <RadioGroupPrimitive.Root ref={ref} className={cn('grid gap-1', className)} {...rest} />;
});

export interface RadioItemProps extends ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Item> {
  label: ReactNode;
  description?: ReactNode;
}

/** Opción de un grupo de radios: círculo de 20 px y fila táctil de 44 px. */
export const RadioItem = forwardRef<HTMLButtonElement, RadioItemProps>(function RadioItem(
  { label, description, className, id, ...rest },
  ref,
) {
  const autoId = useId();
  const itemId = id ?? autoId;
  const descId = description ? `${itemId}-desc` : undefined;
  return (
    <div className={cn('flex min-h-11 items-start gap-3 py-2', className)}>
      <RadioGroupPrimitive.Item
        ref={ref}
        id={itemId}
        aria-describedby={descId}
        className={cn(
          'mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full border border-input bg-card',
          'transition-colors duration-fast ease-standard disabled:cursor-not-allowed disabled:opacity-50 data-[state=checked]:border-primary',
        )}
        {...rest}
      >
        <RadioGroupPrimitive.Indicator className="size-2.5 rounded-full bg-primary" />
      </RadioGroupPrimitive.Item>
      <div className="min-w-0">
        <label htmlFor={itemId} className="block cursor-pointer text-base text-fg">
          {label}
        </label>
        {description ? (
          <p id={descId} className="text-sm text-fg-muted">
            {description}
          </p>
        ) : null}
      </div>
    </div>
  );
});
